import { type ShopperIntentEventDetail } from "@redotech/redo-model/events/intent/shopper-intent";

export function fireIntentEvent(detail: ShopperIntentEventDetail): void {
  document.body.dispatchEvent(
    new CustomEvent(detail.type, { bubbles: true, detail: detail.data }),
  );
}

export type NavigationCallback = () => void;

const navigationCallbacks: Set<NavigationCallback> = new Set();
let navigationListenerInitialized = false;
let originalPushState: typeof history.pushState | null = null;
let originalReplaceState: typeof history.replaceState | null = null;

function handleNavigation(): void {
  for (const cb of navigationCallbacks) cb();
}

function initNavigationListener(): void {
  if (navigationListenerInitialized) {
    return;
  }
  navigationListenerInitialized = true;

  window.addEventListener("popstate", handleNavigation);

  originalPushState = history.pushState.bind(history);
  originalReplaceState = history.replaceState.bind(history);

  history.pushState = (...args) => {
    originalPushState?.(...args);
    handleNavigation();
  };

  history.replaceState = (...args) => {
    originalReplaceState?.(...args);
    handleNavigation();
  };
}

export function subscribeToNavigation(
  callback: NavigationCallback,
): () => void {
  initNavigationListener();
  navigationCallbacks.add(callback);

  return () => {
    navigationCallbacks.delete(callback);
  };
}

export function cleanupNavigationListener(): void {
  if (!navigationListenerInitialized) {
    return;
  }

  window.removeEventListener("popstate", handleNavigation);

  if (originalPushState) {
    history.pushState = originalPushState;
  }
  if (originalReplaceState) {
    history.replaceState = originalReplaceState;
  }

  navigationCallbacks.clear();
  navigationListenerInitialized = false;
  originalPushState = null;
  originalReplaceState = null;
}
